import { Credit, type OnDemandContent } from "@ca.vmedia.tv/domains";
import { RelatedOnDemandCardData, type CardData, CreditCardData } from "@/components/Cards/types";
import { AppConstants } from "@/common";
import { Services } from "@ca.vmedia.tv/services";
import { useDetailPageStore } from "@/stores/DetailPage";
import { Logger } from '@/utils';
import { useFetchRelatedContent } from '@/composables/useFetchRelatedContent';
import { useEmptyLine } from "@/composables/useEmptyLine";
import type { ExposeInterface } from './types';
import type { AxiosError } from 'axios';

export const useLinesDetailMovieStore = defineStore('LinesDetailMovie', (): ExposeInterface => {   
    const { 
        emptyOnDemandContent
    } = useEmptyLine();

    const DetailPageStore = useDetailPageStore();
    const {
        getCurrentItemDetail,
    } = storeToRefs(DetailPageStore);

    const _cast: Ref<Credit[] | null> = ref(null);
    const fetchCast = async (): Promise<void> => {
        try {
            
            if (!getCurrentItemDetail.value?.id || !getCurrentItemDetail.value?.contentType) {
                throw new Error(`No item detail: ${getCurrentItemDetail.value}`);
            }
            const credits = await Services.Instance.credits.getCredits(getCurrentItemDetail.value.id, getCurrentItemDetail.value?.contentType);
            _cast.value = credits.filter((elem, index, self) => self.findIndex((credit: Credit) => Credit.getId(credit) === Credit.getId(elem)) === index);
        } catch (error) {
            _cast.value = [];
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const getCardsCast: ComputedRef<CardData[] | null> = computed(() => {
        const data = _cast.value;

        if (data === null) {
            return null;
        }

        if (data.length === 0) {
            return [];
        }

        return data.map((item) => new CreditCardData(item));
    }); 

    const _relatedContent: Ref<OnDemandContent | null> = ref(null);
    const fetchRelatedContent = async (): Promise<void> => {
        try {
            _relatedContent.value = await useFetchRelatedContent(getCurrentItemDetail.value, AppConstants.ItemsInLine);
        } catch (error) {
            _relatedContent.value = emptyOnDemandContent();
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const getCardsRelatedContent: ComputedRef<CardData[] | null> = computed(() => {
        if (_relatedContent.value === null) {
            return null;
        }

        if (!_relatedContent.value.items || _relatedContent.value.items.length === 0) {
            return [];
        }

        return _relatedContent.value.items.map((item) => new RelatedOnDemandCardData(item, String(getCurrentItemDetail.value?.id)));
    }); 

    const _counterRows: Ref<number> = ref(AppConstants.DetailCounterLines);
    const getCounterLinesDetailMovie: ComputedRef<number> = computed(() => _counterRows.value);
    const _clearCounter = (): void => {
        _counterRows.value = AppConstants.DetailCounterLines;
    };
    const increaseCounterLinesDetailMovie = (): void => {
        _counterRows.value += 1;
    };

    const clearAllLinesDetailMovie = (): void => {
        _clearCounter();
        _cast.value = null;
        _relatedContent.value = null;
    };


    return {
        getCardsCast,
        getCardsRelatedContent,
        getCounterLinesDetailMovie,
        fetchCast,
        fetchRelatedContent,
        increaseCounterLinesDetailMovie,
        clearAllLinesDetailMovie
    };
});
